import DataSeriesModal from "$root/components/DataSeriesModal";
import { IconWalls } from "$root/components/IconWall";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import useWidgetsData from "$root/hooks/useWidgetsData";
import ChartStepperLined from "$root/ui-lib/charts/ChartStepperLined";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { CircularProgressBar, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useTranslation } from "react-i18next";
import "./style.css";

type IndicatorsVariants = "equity_indicators" | "fixed_income_indicators" | "commodities_indicators";

const AssetClassOverviewBlock = (): React.JSX.Element => {
	const { t } = useTranslation();
	const { currentWidgetsData } = useWidgetsData();

	const overviewQuery = outlookRepository.overview.useQuery();
	const {
		data: indicators,
		isLoading,
		isError,
	} = useMappedQuery(
		(data) => ({
			equity_indicators: data.equityIndicators,
			fixed_income_indicators: data.fixedIncomeIndicators,
			commodities_indicators: data.commoditiesIndicators,
		}),
		[],
		overviewQuery,
	);

	useWidgetOptions(
		() => ({
			tooltip: t("ASSET_CLASS_OVERVIEW.TOOLTIP"),
			actionHeader: <DataSeriesModal typeMode="DASHBOARD" splitted={false} />,
			title: t("ASSET_CLASS_OVERVIEW.TITLE"),
		}),
		[t],
	);

	return (
		<>
			{isLoading ? (
				<CircularProgressBar accentColor={themeCSSVars.palette_P400} className="w-full" value="indeterminate" />
			) : isError ? (
				<IconWalls.ErrorData />
			) : (
				indicators && (
					<div className="grid grid-cols-3 gap-6">
						{(["equity_indicators", "fixed_income_indicators", "commodities_indicators"] as IndicatorsVariants[])
							.filter((ind) => indicators[ind])
							.map((indicator, i) =>
								(() => {
									const data = indicators[indicator];
									const { currentValue, prevValue } = data ?? {};
									if (currentValue?.value === undefined) {
										return <></>;
									}
									const sanitizedPrevValue = prevValue?.value === currentValue.value ? undefined : prevValue?.value;
									const indicatorStrings = t(`OUTLOOK.indicators.${indicator}`, { returnObjects: true });
									const labels = indicatorStrings.labels;
									return (
										<div key={i} className="grow">
											<Text type="Body/M/Bold" className="mb-2">
												{indicatorStrings.title}
											</Text>
											<div className="h-20 flex items-center">
												<ChartStepperLined
													columnNumber={5}
													current={{
														value: currentValue.value - 1,
														tooltip: [
															{
																label: t("CURRENT", { returnObjects: false }),
																value: indicatorStrings.tooltips[currentValue.value - 1],
																data: currentValue?.data,
															},
														],
													}}
													previous={{
														value: sanitizedPrevValue ? sanitizedPrevValue - 1 : sanitizedPrevValue,
														tooltip: sanitizedPrevValue
															? [
																	{
																		label: t("PREVIOUS"),
																		value: indicatorStrings.tooltips[sanitizedPrevValue - 1],
																		data: prevValue?.data,
																	},
																]
															: [],
													}}
													labels={labels}
													line="dotted"
													lastDashboardUpdate={currentWidgetsData.creation_time}
												/>
											</div>
										</div>
									);
								})(),
							)}
					</div>
				)
			)}
		</>
	);
};

export default AssetClassOverviewBlock;
